import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { WizardShell } from '../registration/components/WizardShell';
import { FormField } from '../registration/components/FormField';
import { PasswordField } from '../registration/components/PasswordField';
import { StepActions } from '../registration/components/StepActions';
import { Alert } from '../registration/components/Alert';
import { isAcceptablePassword, isValidPhone, maskPhone } from '../registration/utils';
import { SocialConfirmButtons } from './components/SocialConfirmButtons';
import {
  fetchAccountConfirmation,
  completeAccountConfirmation,
  AccountConfirmationApiError,
} from '../../../services/accountConfirmationApi';

type LoadState = 'loading' | 'ready' | 'invalid';

/**
 * AccountConfirmationPage - Conclusão de cadastro para usuários criados
 * pelo admin (convite via painel).
 *
 * O e-mail de confirmação do Devise leva para `/condomino/confirmacao`
 * (backend), que redireciona para cá com `?token=...`. Diferente do
 * auto-cadastro (RegistrationPage, que usa código por e-mail), aqui o
 * usuário já existe sem senha — esta página só completa o cadastro
 * (nome, telefone, senha) e confirma a conta via AccountConfirmationsController.
 *
 * Reaproveita o mesmo shell/campos/ações do wizard de auto-cadastro para
 * manter um único visual entre os dois fluxos de conta.
 */
export function AccountConfirmationPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadState('invalid');
      return;
    }

    fetchAccountConfirmation(token)
      .then((info) => {
        setEmail(info.email);
        setFirstName(info.firstName);
        setLastName(info.lastName);
        setPhone(info.phoneNumber ? maskPhone(info.phoneNumber) : '');
        setLoadState('ready');
      })
      .catch(() => setLoadState('invalid'));
  }, [token]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!firstName.trim()) errs.firstName = t('views.registration.user_data.first_name_error', 'Informe seu nome.');
    if (!lastName.trim()) errs.lastName = t('views.registration.user_data.last_name_error', 'Informe seu sobrenome.');
    if (!isValidPhone(phone)) errs.phone = t('views.registration.user_data.phone_error', 'Informe um celular válido com DDD.');
    if (!isAcceptablePassword(password)) errs.password = t('views.registration.user_data.password_error', 'A senha deve ter ao menos 8 caracteres.');
    if (confirm !== password) errs.confirm = t('views.registration.user_data.confirm_error', 'As senhas não conferem.');
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!validate()) return;

    setSubmitting(true);
    try {
      await completeAccountConfirmation({
        token,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: phone,
        password,
        passwordConfirmation: confirm,
      });
      navigate('/login', {
        state: { flash: { type: 'success', message: t('views.account_confirmation.success', 'Cadastro concluído com sucesso. Faça login para continuar.') } },
      });
    } catch (err) {
      const message =
        err instanceof AccountConfirmationApiError ? err.message : t('views.registration.user_data.submit_error', 'Não foi possível concluir o cadastro. Tente novamente.');
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadState === 'loading') {
    return (
      <WizardShell steps={[]} currentIndex={0} title={t('views.account_confirmation.title', 'Concluir cadastro')} hideStepper>
        <p className="text-center text-[13px] text-navy-700/70 py-6">{t('common.loading', 'Carregando...')}</p>
      </WizardShell>
    );
  }

  if (loadState === 'invalid') {
    return (
      <WizardShell steps={[]} currentIndex={0} title={t('views.account_confirmation.invalid_title', 'Link inválido')} hideStepper>
        <Alert
          type="error"
          message={t('views.account_confirmation.invalid_text', 'Este link de confirmação é inválido ou já foi utilizado. Solicite um novo convite ou faça login se já concluiu seu cadastro.')}
        />
        <Link
          to="/login"
          className="block text-center py-[11px] px-[18px] rounded-[22px] border-0 cursor-pointer text-[13px] font-bold text-navy-900 bg-lime-500 hover:bg-lime-400 transition-colors"
        >
          {t('views.account_confirmation.go_to_login', 'Ir para o login')}
        </Link>
      </WizardShell>
    );
  }

  return (
    <WizardShell
      steps={[]}
      currentIndex={0}
      title={t('views.account_confirmation.title', 'Concluir cadastro')}
      subtitle={
        email
          ? t('views.account_confirmation.subtitle_with_email', { defaultValue: 'Finalize o cadastro de {{email}} definindo seus dados e uma senha.', email })
          : t('views.account_confirmation.subtitle_default', 'Defina seus dados e uma senha para concluir o cadastro.')
      }
      hideStepper
    >
      <SocialConfirmButtons token={token} />

      <form onSubmit={handleSubmit} noValidate autoComplete="on">
        {error && <Alert type="error" message={error} onClose={() => setError('')} />}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
          <FormField
            label={t('views.devise.registrations.name', 'Nome')}
            icon="fa-user"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            error={fieldErrors.firstName}
            required
            autoFocus
          />
          <FormField
            label={t('views.devise.registrations.last_name', 'Sobrenome')}
            icon="fa-user"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            error={fieldErrors.lastName}
            required
          />
        </div>

        <FormField
          label={t('views.devise.registrations.cellphone', 'Celular')}
          type="tel"
          icon="fa-phone"
          autoComplete="tel"
          placeholder="(00) 00000-0000"
          value={phone}
          onChange={(e) => setPhone(maskPhone(e.target.value))}
          error={fieldErrors.phone}
          maxLength={16}
          required
        />

        <PasswordField
          label={t('views.login.password_placeholder', 'Senha')}
          value={password}
          onChange={setPassword}
          error={fieldErrors.password}
          hint={t('views.registration.user_data.password_hint', 'Use ao menos 8 caracteres, com letras e números.')}
          showStrength
          required
        />

        <PasswordField label={t('views.devise.registrations.confirm_password', 'Confirme a senha')} value={confirm} onChange={setConfirm} error={fieldErrors.confirm} required />

        <StepActions submitLabel={t('views.account_confirmation.submit', 'Concluir cadastro')} loading={submitting} loadingLabel={t('views.registration.sending', 'Enviando...')} />
      </form>
    </WizardShell>
  );
}
